/**
 * Manages persistent bids on player chocobos
 */
export class BidManager {
  static BID_EXPIRATION_DAYS = 10
  
  /**
   * Create a new bid on a player chocobo
   * @param {Object} chocobo - The chocobo being bid on
   * @param {Object} bidder - The NPC making the bid
   * @param {number} amount - Bid amount in Gil
   * @param {number} currentDay - Current game day
   * @returns {Object} The bid object
   */
  static createBid(chocobo, bidder, amount, currentDay) {
    return {
      id: crypto.randomUUID(),
      chocoboId: chocobo.id,
      chocoboName: chocobo.name,
      bidderId: bidder.id,
      bidderName: bidder.name,
      bidderRanchName: bidder.ranchName,
      amount: amount,
      createdDay: currentDay,
      expiresDay: currentDay + this.BID_EXPIRATION_DAYS,
      status: 'pending' // pending, accepted, rejected, expired
    }
  }
  
  /**
   * Process daily bid updates (expire old bids)
   * @param {Object} gameStore - Game store instance
   */
  static processDailyBids(gameStore) {
    if (!gameStore.activeBids) return
    
    gameStore.activeBids.forEach(bid => {
      if (bid.status === 'pending' && gameStore.currentDay >= bid.expiresDay) {
        bid.status = 'expired'
      }
    })
    
    // Clean up very old bids (30+ days)
    gameStore.activeBids = gameStore.activeBids.filter(bid => 
      gameStore.currentDay - bid.createdDay < 30
    )
  }
  
  /**
   * Get active bids for player chocobos
   * @param {Object} gameStore - Game store instance
   * @returns {Array} Active bids
   */
  static getActiveBids(gameStore) {
    if (!gameStore.activeBids) return []
    
    return gameStore.activeBids.filter(bid => 
      bid.status === 'pending' &&
      gameStore.playerOwner.chocoboIds.includes(bid.chocoboId)
    )
  }
  
  /**
   * Get bid summary for display
   * @param {Object} gameStore - Game store instance
   * @returns {Object} Summary of bids
   */
  static getBidSummary(gameStore) {
    const activeBids = this.getActiveBids(gameStore)
    
    // Group by chocobo
    const bidsByChocobo = {}
    activeBids.forEach(bid => {
      if (!bidsByChocobo[bid.chocoboId]) {
        bidsByChocobo[bid.chocoboId] = []
      }
      bidsByChocobo[bid.chocoboId].push(bid)
    })
    
    // Get highest bid for each chocobo
    const highestBids = Object.values(bidsByChocobo).map(bids => {
      return bids.sort((a, b) => b.amount - a.amount)[0]
    })
    
    return {
      totalActiveBids: activeBids.length,
      chocobosWithBids: Object.keys(bidsByChocobo).length,
      highestBids: highestBids,
      expiringToday: activeBids.filter(bid => 
        bid.expiresDay - gameStore.currentDay === 1
      ).length
    }
  }
  
  /**
   * Accept a bid
   * @param {string} bidId - The bid ID
   * @param {Object} gameStore - Game store instance
   * @returns {boolean} Success status
   */
  static acceptBid(bidId, gameStore) {
    const bid = gameStore.activeBids.find(b => b.id === bidId)
    if (!bid || bid.status !== 'pending') return false
    
    // Find chocobo and bidder
    const chocobo = gameStore.chocobos.find(c => c.id === bid.chocoboId)
    const bidder = gameStore.owners.find(o => o.id === bid.bidderId)
    
    if (!chocobo || !bidder) return false
    
    // Validate bidder still has funds
    if (bidder.money < bid.amount) {
      bid.status = 'rejected'
      bid.rejectionReason = 'Insufficient funds'
      return false
    }
    
    // Transfer chocobo
    const playerIndex = gameStore.playerOwner.chocoboIds.indexOf(chocobo.id)
    if (playerIndex === -1) return false
    
    // Remove from player
    gameStore.playerOwner.chocoboIds.splice(playerIndex, 1)
    
    // Add to bidder
    bidder.chocoboIds.push(chocobo.id)
    
    // Transfer money
    bidder.money -= bid.amount
    gameStore.playerOwner.money += bid.amount
    
    // Update bid status
    bid.status = 'accepted'
    bid.acceptedDay = gameStore.currentDay
    
    // Reject all other bids for this chocobo
    gameStore.activeBids.forEach(otherBid => {
      if (otherBid.chocoboId === bid.chocoboId && 
          otherBid.id !== bid.id && 
          otherBid.status === 'pending') {
        otherBid.status = 'rejected'
        otherBid.rejectionReason = 'Another bid was accepted'
      }
    })
    
    // Note: Bid notifications are now handled by the BidNotifications component
    // No need to add to general notifications
    
    return true
  }
  
  /**
   * Reject a bid
   * @param {string} bidId - The bid ID
   * @param {Object} gameStore - Game store instance
   * @returns {boolean} Success status
   */
  static rejectBid(bidId, gameStore) {
    const bid = gameStore.activeBids.find(b => b.id === bidId)
    if (!bid || bid.status !== 'pending') return false
    
    bid.status = 'rejected'
    bid.rejectionReason = 'Owner declined'
    bid.rejectedDay = gameStore.currentDay
    
    return true
  }
  
  /**
   * Generate new bids from NPCs (called periodically)
   * @param {Object} gameStore - Game store instance
   */
  static generateNewBids(gameStore) {
    if (!gameStore.activeBids) gameStore.activeBids = []
    
    const playerChocobos = gameStore.playerChocobos
    const activeBidsByChocobo = {}
    
    // Track existing bids
    this.getActiveBids(gameStore).forEach(bid => {
      if (!activeBidsByChocobo[bid.chocoboId]) {
        activeBidsByChocobo[bid.chocoboId] = 0
      }
      activeBidsByChocobo[bid.chocoboId]++
    })
    
    // Generate new bids
    playerChocobos.forEach(chocobo => {
      // Skip if already has 3+ active bids
      if (activeBidsByChocobo[chocobo.id] >= 3) return
      
      // Skip young chocobos
      if (chocobo.level < 5) return
      
      // Skip chocobos marked as not for sale
      if (chocobo.notForSale) return
      
      // Generate bids from interested NPCs
      const interestedNPCs = gameStore.owners.filter(owner => {
        if (owner.isPlayer) return false
        if (owner.money < 10000) return false
        
        // Check if NPC would be interested
        return this.isNPCInterested(owner, chocobo, gameStore)
      })
      
      // Random chance for each interested NPC to bid
      interestedNPCs.forEach(npc => {
        if (Math.random() < 0.03) { // 3% chance per day (reduced from 10% to prevent spam)
          const bidAmount = this.calculateBidAmount(npc, chocobo)
          
          if (bidAmount > 0 && npc.money >= bidAmount) {
            const bid = this.createBid(chocobo, npc, bidAmount, gameStore.currentDay)
            gameStore.activeBids.push(bid)
            
            // Note: New bid notifications are now handled by the BidNotifications component
            // The bottom-left notification area will show all active bids
          }
        }
      })
    })
  }
  
  /**
   * Check if NPC is interested in a chocobo
   * @private
   */
  static isNPCInterested(npc, chocobo, gameStore) {
    const ownedChocobos = gameStore.getChocobosByOwnerId(npc.id)
    
    // Don't buy if stable is full
    if (ownedChocobos.length >= 15) return false
    
    // Check if chocobo fills a need
    const hasColorAlready = ownedChocobos.some(c => c.color === chocobo.color)
    const needsUpgrade = !ownedChocobos.some(c => 
      c.speed >= chocobo.speed && c.stamina >= chocobo.stamina
    )
    
    // Interested if: rare color they don't have, or significant upgrade
    return (!hasColorAlready && ['black', 'white', 'gold', 'red'].includes(chocobo.color)) ||
           (needsUpgrade && chocobo.level >= 10)
  }
  
  /**
   * Calculate bid amount based on NPC wealth and chocobo value
   * @private
   */
  static calculateBidAmount(npc, chocobo) {
    // Base value calculation
    let baseValue = 5000
    baseValue += chocobo.level * 1000
    baseValue += (chocobo.speed + chocobo.stamina) * 100
    
    // Color multiplier
    const colorMultipliers = {
      yellow: 1.0,
      blue: 1.2,
      green: 1.2,
      pink: 1.3,
      black: 1.5,
      white: 1.8,
      red: 1.8,
      gold: 2.5
    }
    baseValue *= (colorMultipliers[chocobo.color] || 1.0)
    
    // X-factor bonus
    if (chocobo.hasSpeedXFactor) baseValue *= 1.3
    if (chocobo.hasStaminaXFactor) baseValue *= 1.3
    
    // NPC budget (won't bid more than 30% of their wealth)
    const maxBid = Math.floor(npc.money * 0.3)
    
    // Competitiveness factor
    const bidAmount = Math.floor(baseValue * (0.8 + Math.random() * 0.4))
    
    return Math.min(bidAmount, maxBid)
  }
}